import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, Input, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { forkJoin, Subscription } from 'rxjs';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { environment } from '../../../../../my-app/src/environments/environment';

interface HeaderColumnConfig {
  key: string;
  label?: string;
  type?: 'text' | 'dropdown' | 'input';
  freeze?: boolean;
  frozen?: boolean;
  sticky?: boolean;
  isDropdown?: boolean;
  options?: string[];
  optionsField?: string;
  isMultiSelect?: boolean;
  placeholder?: string;
  inputType?: string;
  edit?: boolean;
  editable?: boolean;
}

interface NormalizedHeaderColumnConfig {
  key: string;
  label: string;
  config: HeaderColumnConfig;
}

interface LegacyDiscountMappingConfig {
  discountType: string;
  quoteLevelDiscount: string;
  reasons: string[];
  defaultSelectedReason?: string;
  defaultSelectedReasons?: string[];
}

interface SelectionMappingConfig {
  value?: string;
  triggerValue?: string;
  selectionValue?: string;
  row?: RowData;
  rows?: RowData[];
}

interface AddRowButtonConfig {
  enabled?: boolean;
  label?: string;
  insertAt?: 'start' | 'end' | 'top' | 'bottom';
  defaults?: RowData;
  row?: RowData;
  rowTemplate?: RowData;
}

interface FooterButtonConfig {
  enabled?: boolean;
  label?: string;
  loadingLabel?: string;
  submitApi?: FooterSubmitApiConfig | FooterSubmitApiConfig[];
  apiUrl?: string;
  url?: string;
  method?: string;
  payload?: Record<string, any>;
  requestDataKey?: string;
  includeColumns?: string[];
  excludeColumns?: string[];
  rowFieldMap?: Record<string, string>;
  saveDirtyRowsOnly?: boolean;
  successMessage?: string;
  errorMessage?: string;
}

interface FooterSubmitApiConfig {
  apiUrl?: string;
  url?: string;
  method?: string;
  payload?: Record<string, any>;
  requestDataKey?: string;
  includeColumns?: string[];
  excludeColumns?: string[];
  rowFieldMap?: Record<string, string>;
  saveDirtyRowsOnly?: boolean;
  successMessage?: string;
  errorMessage?: string;
}

interface RowDeleteButtonConfig {
  enabled?: boolean;
  label?: string;
  loadingLabel?: string;
  removeRowOnSuccess?: boolean;
  submitApi?: FooterSubmitApiConfig;
  apiUrl?: string;
  url?: string;
  method?: string;
  payload?: Record<string, any>;
  requestDataKey?: string;
  includeColumns?: string[];
  excludeColumns?: string[];
  rowFieldMap?: Record<string, string>;
  successMessage?: string;
  errorMessage?: string;
}

type RowData = Record<string, any>;

@Component({
  selector: 'app-table-w-dropdown-2',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTableModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatIconModule, MatSnackBarModule],
  templateUrl: './table-w-dropdown-2.component.html',
  styleUrl: './table-w-dropdown-2.component.scss'
})
export class TableWDropdownComponent2 implements OnDestroy {
  private readonly previousRouteStorageKey = 'codex.previous-route';
  private _data: any;
  private filterSubscription?: Subscription;
  private widgetDataSubscription?: Subscription;
  private resetStateSubscription?: Subscription;
  private apiDataSubscription?: Subscription;
  private widgetRefreshSubscription?: Subscription;
  private lastApiRequestSignature = '';
  private inFlightApiRequestSignature = '';
  private baseDataSource: RowData[] = [];
  private selectedRowsByValue: Map<string, RowData[]> = new Map();
  private mappingRowsByValue: Record<string, RowData[]> = {};
  private dynamicFilterKey = '';
  private selectionMode: 'replace' | 'append' = 'replace';
  private lastFilterSelectionSignature = '';
  private enabledEditableColumns = new Set<string>();
  private dirtyRowIds = new Set<string>();
  private initialLoadPayloadOverride: Record<string, any> | null = null;
  private cellConfigField = '__cellConfig';
  private loadingButtons = new Set<string>();
  private readonly internalRowIdField = '__row_local_id';
  private readonly manualRowFlagField = '__manual_added_row';
  readonly rowRemoveActionColumn = '__row_remove_action';
  private readonly snackBar = inject(MatSnackBar);
  private deletingRowIds = new Set<string>();
  private selectedDeleteRowIds = new Set<string>();
  private rowIdSequence = 0;
  private suppressSelfWidgetDataSync = false;
  private lastPublishedWidgetDataSignature = '';

  @Input() set data(value: any) {
    this._data = value;
    this.initializeTable();
  }
  get data(): any {
    return this._data;
  }

  displayedColumns: string[] = [];
  columnLabels: Record<string, string> = {};
  dataSource: RowData[] = [];
  dropdownColumnConfig: Record<string, HeaderColumnConfig> = {};
  columnConfigMap: Record<string, HeaderColumnConfig> = {};
  private readonly defaultFooterLineText = 'Approval triggers if cumulative discounts greater than 20%';
  private readonly defaultTableViewportOffsetPx = 300;
  private readonly defaultTableMinHeightPx = 140;
  private readonly defaultTableMaxHeightPx = 560;
  private readonly tableHeaderHeightPx = 40;
  private readonly tableRowHeightPx = 48;
  private readonly compactColumnKeys = new Set<string>([
    'line_discount',
    'quote_level_discount'
  ]);
  private readonly wideColumnKeys = new Set<string>([
    'condition',
    'description'
  ]);
  private readonly textColumnKeys = new Set<string>([
    'part_number'
  ]);
  private readonly numericColumnHints = [
    'line_discount',
    'quote_level_discount',
    'discounted_price',
    'unit_price',
    'price',
    'amount',
    'sales',
    'impact',
    'total',
    'qty',
    'quantity',
    'rate',
    'percent',
    'percentage',
    'usd'
  ];
  private readonly nonNumericColumnHints = [
    'discount_type',
    'type',
    'reason',
    'description',
    'name',
    'clause',
    'condition',
    'bundle'
  ];

  constructor(
    private globalFilterService: GlobalFilterService,
    private http: HttpClient
  ) { }

  get shouldShowFooterLine(): boolean {
    const rawValue = this.data?.add_line ?? this.data?.addLine;

    if (typeof rawValue === 'string') {
      const normalized = rawValue.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    if (typeof rawValue === 'number') {
      return rawValue === 1;
    }

    return rawValue === true;
  }

  get footerLineText(): string {
    const configuredText = String(this.data?.add_line_text ?? this.data?.addLineText ?? '').trim();
    return configuredText || this.defaultFooterLineText;
  }

  get showAddRowButton(): boolean {
    const config = this.resolveAddRowButtonConfig();
    const explicit = this.parseBooleanFlag(config.enabled);
    if (explicit !== null) {
      return explicit;
    }

    const fallback = this.parseBooleanFlag(
      this.data?.enableAddRow ?? this.data?.allowManualRowAdd ?? this.data?.manualRowEntryEnabled
    );
    return fallback === true;
  }

  get addRowButtonLabel(): string {
    const config = this.resolveAddRowButtonConfig();
    const configured = String(
      config.label ?? this.data?.addRowLabel ?? this.data?.manualRowButtonLabel ?? ''
    ).trim();
    return configured || 'Add Row';
  }

  get showInlineAddRowButton(): boolean {
    return this.showAddRowButton && this.showFooterButton;
  }

  get tableRenderColumns(): string[] {
    return this.showRowSelectionColumn
      ? [...this.displayedColumns, this.rowRemoveActionColumn]
      : this.displayedColumns;
  }

  get showRowSelectionColumn(): boolean {
    return this.showRowDeleteButton;
  }

  get showRowDeleteButton(): boolean {
    const config = this.resolveRowDeleteButtonConfig();
    const explicit = this.parseBooleanFlag(config.enabled);
    if (explicit !== null) {
      return explicit;
    }

    return !!this.normalizeRowDeleteSubmitApiConfig(config);
  }

  get showFooterButton(): boolean {
    return this.footerButtonConfigs.length > 0;
  }

  get footerButtonConfigs(): FooterButtonConfig[] {
    return this.resolveFooterButtonConfigs().filter((config) => this.shouldRenderFooterButton(config));
  }

  getFooterButtonLabel(config: FooterButtonConfig): string {
    return String(config.label || '').trim() || 'Save';
  }

  getFooterButtonRenderLabel(config: FooterButtonConfig): string {
    const label = this.getFooterButtonLabel(config);
    return this.loadingButtons.has(label) ? (String(config.loadingLabel || '').trim() || 'Saving...') : label;
  }

  isFooterButtonLoading(config: FooterButtonConfig): boolean {
    return this.loadingButtons.has(this.getFooterButtonLabel(config));
  }

  get tableViewportMaxHeightPx(): number | null {
    if (!this.isAutoTableHeightEnabled()) {
      return null;
    }

    return this.resolveEffectiveTableMaxHeightPx();
  }

  get tableViewportHeightPx(): number | null {
    if (!this.isAutoTableHeightEnabled()) {
      return null;
    }

    const effectiveMaxHeight = this.resolveEffectiveTableMaxHeightPx();
    const minHeight = Math.min(this.resolveConfiguredTableMinHeightPx(), effectiveMaxHeight);
    const rowsCount = Array.isArray(this.dataSource) ? this.dataSource.length : 0;
    const contentHeight = this.tableHeaderHeightPx + (rowsCount * this.tableRowHeightPx) + 2;
    return Math.max(minHeight, Math.min(contentHeight, effectiveMaxHeight));
  }

  onAddRowClick(event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.showAddRowButton) {
      return;
    }

    const config = this.resolveAddRowButtonConfig();
    const manualRow = this.createManualRow(config);
    const insertAtStart = this.shouldInsertManualRowAtStart(config);
    manualRow[this.manualRowFlagField] = true;
    this.markRowAsDirty(manualRow);

    this.baseDataSource = insertAtStart
      ? [manualRow, ...this.baseDataSource]
      : [...this.baseDataSource, manualRow];

    this.rebuildTableData();
  }

  onRemoveManualRowClick(row: RowData, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.canRemoveManualRow(row)) {
      return;
    }

    const rowId = this.getInternalRowId(row);
    if (!rowId) {
      return;
    }

    this.removeRowById(rowId);
  }

  isManualAddedRow(row: RowData): boolean {
    return !!row && typeof row === 'object' && this.parseBooleanFlag(row[this.manualRowFlagField]) === true;
  }

  canRemoveManualRow(row: RowData): boolean {
    return this.isManualAddedRow(row) && this.isRowDirty(row);
  }

  canDeleteRow(row: RowData): boolean {
    return this.showRowDeleteButton && !this.canRemoveManualRow(row) && !!this.normalizeRowDeleteSubmitApiConfig(this.resolveRowDeleteButtonConfig());
  }

  getRowDeleteButtonLabel(row: RowData): string {
    const config = this.resolveRowDeleteButtonConfig();
    const rowId = this.getInternalRowId(row);
    if (rowId && this.deletingRowIds.has(rowId)) {
      return String(config.loadingLabel || '').trim() || 'Deleting...';
    }

    return String(config.label || '').trim() || 'Delete';
  }

  isRowDeleteLoading(row: RowData): boolean {
    const rowId = this.getInternalRowId(row);
    return !!rowId && this.deletingRowIds.has(rowId);
  }

  getFooterDeleteButtonLabel(): string {
    const config = this.resolveRowDeleteButtonConfig();
    return this.deletingRowIds.size > 0
      ? (String(config.loadingLabel || '').trim() || 'Deleting...')
      : (String(config.label || '').trim() || 'Delete');
  }

  isFooterDeleteDisabled(): boolean {
    return this.selectedDeleteRowIds.size === 0 || this.deletingRowIds.size > 0;
  }

  isRowChecked(row: RowData): boolean {
    const rowId = this.getInternalRowId(row);
    return !!rowId && this.selectedDeleteRowIds.has(rowId);
  }

  areAllRowsChecked(): boolean {
    const selectableRows = this.getSelectableRows();
    return selectableRows.length > 0 && selectableRows.every((row) => this.isRowChecked(row));
  }

  isSomeRowChecked(): boolean {
    return this.selectedDeleteRowIds.size > 0 && !this.areAllRowsChecked();
  }

  onRowCheckboxChange(row: RowData, event: Event): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    const rowId = this.ensureInternalRowId(row);
    if (!rowId) {
      return;
    }

    if (input.checked) {
      this.selectedDeleteRowIds.add(rowId);
      return;
    }

    this.selectedDeleteRowIds.delete(rowId);
  }

  onSelectAllRowsChange(event: Event): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    this.selectedDeleteRowIds.clear();
    if (!input.checked) {
      return;
    }

    this.getSelectableRows().forEach((row) => {
      const rowId = this.ensureInternalRowId(row);
      if (rowId) {
        this.selectedDeleteRowIds.add(rowId);
      }
    });
  }

  onFooterDeleteClick(event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.isFooterDeleteDisabled()) {
      return;
    }

    const config = this.resolveRowDeleteButtonConfig();
    const apiConfig = this.normalizeRowDeleteSubmitApiConfig(config);
    if (!apiConfig) {
      return;
    }

    const selectedRows = this.getSelectedDeleteRows();
    const manualRows = selectedRows.filter((row) => this.canRemoveManualRow(row));
    const apiRows = selectedRows.filter((row) => !this.canRemoveManualRow(row));
    const requests = apiRows
      .map((row) => {
        const payload = this.buildFooterSubmitPayload(apiConfig, row);
        return this.buildFooterSubmitRequest(apiConfig, payload);
      })
      .filter((request) => !!request);

    if (apiRows.length > 0 && requests.length === 0) {
      this.showMessage(String(config.errorMessage || apiConfig.errorMessage || 'Delete API is not configured.'));
      return;
    }

    selectedRows.forEach((row) => {
      const rowId = this.ensureInternalRowId(row);
      if (rowId) {
        this.deletingRowIds.add(rowId);
      }
    });

    if (manualRows.length > 0) {
      manualRows.forEach((row) => this.removeRowById(this.getInternalRowId(row)));
    }

    if (apiRows.length === 0) {
      this.clearDeleteSelection();
      this.showMessage('Selected row removed.');
      return;
    }

    forkJoin(requests).subscribe({
      next: () => this.handleBulkDeleteSuccess(apiRows, config, apiConfig),
      error: (error: any) => this.handleBulkDeleteError(selectedRows, config, apiConfig, error)
    });
  }

  private removeRowById(rowId: string): void {
    if (!rowId) {
      return;
    }

    this.baseDataSource = this.baseDataSource.filter((sourceRow) => this.getInternalRowId(sourceRow) !== rowId);
    this.dataSource = this.dataSource.filter((sourceRow) => this.getInternalRowId(sourceRow) !== rowId);
    this.dirtyRowIds.delete(rowId);
    this.selectedDeleteRowIds.delete(rowId);
    this.deletingRowIds.delete(rowId);
    this.rebuildTableData();
  }

  private getSelectableRows(): RowData[] {
    return (Array.isArray(this.dataSource) ? this.dataSource : [])
      .filter((row) => !!row && typeof row === 'object' && !Array.isArray(row));
  }

  private getSelectedDeleteRows(): RowData[] {
    return this.getSelectableRows().filter((row) => this.isRowChecked(row));
  }

  private clearDeleteSelection(): void {
    this.selectedDeleteRowIds.clear();
    this.deletingRowIds.clear();
  }

  private trimDeleteSelection(): void {
    const visibleRowIds = new Set(
      this.getSelectableRows()
        .map((row) => this.getInternalRowId(row))
        .filter((rowId) => !!rowId)
    );

    this.selectedDeleteRowIds.forEach((rowId) => {
      if (!visibleRowIds.has(rowId)) {
        this.selectedDeleteRowIds.delete(rowId);
      }
    });
    this.deletingRowIds.forEach((rowId) => {
      if (!visibleRowIds.has(rowId)) {
        this.deletingRowIds.delete(rowId);
      }
    });
  }

  private handleBulkDeleteSuccess(
    rows: RowData[],
    config: RowDeleteButtonConfig,
    apiConfig: FooterSubmitApiConfig
  ): void {
    rows.forEach((row) => {
      const rowId = this.getInternalRowId(row);
      this.deletingRowIds.delete(rowId);
      if (config.removeRowOnSuccess !== false) {
        this.removeRowById(rowId);
      }
    });
    this.clearDeleteSelection();
    this.showMessage(String(config.successMessage || apiConfig.successMessage || 'Selected rows deleted successfully.'));
  }

  private handleBulkDeleteError(
    rows: RowData[],
    config: RowDeleteButtonConfig,
    apiConfig: FooterSubmitApiConfig,
    error: any
  ): void {
    rows.forEach((row) => {
      const rowId = this.getInternalRowId(row);
      if (rowId) {
        this.deletingRowIds.delete(rowId);
      }
    });
    console.error('[table-w-dropdown-2] Delete API call failed', error);
    this.showMessage(String(config.errorMessage || apiConfig.errorMessage || 'Failed to delete selected rows.'));
  }

  private showMessage(message: string): void {
    const text = String(message || '').trim();
    if (!text) {
      return;
    }

    this.snackBar.open(text, 'Close', { duration: 3000 });
  }

  onFooterButtonClick(config: FooterButtonConfig, event?: MouseEvent): void {
    event?.stopPropagation();
    const buttonLabel = this.getFooterButtonLabel(config);
    if (this.isFooterButtonDisabled(config) || this.loadingButtons.has(buttonLabel)) {
      return;
    }

    const submitApiConfigs = this.resolveFooterSubmitApiConfigs(config);
    if (submitApiConfigs.length === 0) {
      return;
    }

    this.loadingButtons.add(buttonLabel);
    const requests = this.buildFooterSubmitRequests(submitApiConfigs);
    if (requests.length === 0) {
      this.loadingButtons.delete(buttonLabel);
      return;
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.loadingButtons.delete(buttonLabel);
        this.clearDirtyRows();
        this.publishWidgetDataIfEnabled();
      },
      error: (error) => {
        this.loadingButtons.delete(buttonLabel);
        console.error('[table-w-dropdown-2] Save API call failed', error);
      }
    });
  }

  isDropdownColumn(columnKey: string, row?: RowData): boolean {
    const config = this.getCellConfig(row, columnKey);
    if (!config) {
      return false;
    }

    const type = String(config.type || '').trim().toLowerCase();
    return type === 'dropdown' || config.isDropdown === true || !!this.dropdownColumnConfig[columnKey];
  }

  private hasExplicitEditFlag(columnKey: string, row?: RowData): boolean {
    const config = this.getCellConfig(row, columnKey);
    if (!config) {
      return false;
    }

    return typeof config.edit === 'boolean' || typeof config.editable === 'boolean';
  }

  isColumnEditable(columnKey: string, row?: RowData): boolean {
    const config = this.getCellConfig(row, columnKey);
    if (!config) {
      return true;
    }

    if (typeof config.edit === 'boolean') {
      return config.edit ? this.enabledEditableColumns.has(columnKey) : false;
    }

    if (typeof config.editable === 'boolean') {
      return config.editable ? this.enabledEditableColumns.has(columnKey) : false;
    }

    return true;
  }

  canToggleColumnEdit(columnKey: string): boolean {
    const config = this.columnConfigMap[columnKey] || this.dropdownColumnConfig[columnKey];
    if (!config) {
      return false;
    }

    if (typeof config.edit === 'boolean') {
      return config.edit === true;
    }

    if (typeof config.editable === 'boolean') {
      return config.editable === true;
    }

    return false;
  }

  isColumnEditEnabled(columnKey: string): boolean {
    return this.enabledEditableColumns.has(columnKey);
  }

  toggleColumnEdit(columnKey: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.canToggleColumnEdit(columnKey)) {
      return;
    }

    if (this.enabledEditableColumns.has(columnKey)) {
      this.enabledEditableColumns.delete(columnKey);
      return;
    }

    this.enabledEditableColumns.add(columnKey);
  }

  isInputColumn(columnKey: string, row?: RowData): boolean {
    const config = this.getCellConfig(row, columnKey);
    if (!config) {
      return false;
    }

    const type = String(config.type || '').trim().toLowerCase();
    if (type === 'dropdown' || config.isDropdown === true || !!this.dropdownColumnConfig[columnKey]) {
      return false;
    }

    if (type === 'input') {
      return true;
    }

    if (String(config.inputType || '').trim().length > 0) {
      return true;
    }

    return this.hasExplicitEditFlag(columnKey, row) && this.isColumnEditable(columnKey, row);
  }

  isMultiSelect(columnKey: string, row?: RowData): boolean {
    return !!this.getCellConfig(row, columnKey)?.isMultiSelect;
  }

  getInputType(columnKey: string, row?: RowData): string {
    const configured = String(this.getCellConfig(row, columnKey)?.inputType || '').trim();
    if (this.isNumericColumn(columnKey, row)) {
      // Keep numeric fields as text inputs so comma-separated values can be shown/edited.
      return 'text';
    }

    if (configured) {
      return configured;
    }

    return 'text';
  }

  getInputPlaceholder(columnKey: string, row?: RowData): string {
    const configured = String(this.getCellConfig(row, columnKey)?.placeholder || '').trim();
    if (configured) {
      return configured;
    }

    const label = String(this.columnLabels?.[columnKey] || columnKey || '').trim();
    return label ? `Enter ${label}` : 'Enter value';
  }

  public isCompactColumn(columnKey: string): boolean {
    return this.compactColumnKeys.has(String(columnKey || '').trim().toLowerCase());
  }

  isWideColumn(columnKey: string): boolean {
    return this.wideColumnKeys.has(String(columnKey || '').trim().toLowerCase());
  }

  public isNumericColumn(columnKey: string, row?: RowData): boolean {
    if (!columnKey) {
      return false;
    }

    if (this.isTextColumn(columnKey, row)) {
      return false;
    }

    if (this.isLikelyNumericColumn(columnKey)) {
      return true;
    }

    return this.isNumericValue(row?.[columnKey]);
  }

  private isTextColumn(columnKey: string, row?: RowData): boolean {
    const normalizedKey = String(columnKey || '').trim().toLowerCase();
    if (this.textColumnKeys.has(normalizedKey)) {
      return true;
    }

    const inputType = String(this.getCellConfig(row, columnKey)?.inputType || '').trim().toLowerCase();
    return inputType === 'text';
  }

  public getHeaderCellClasses(columnKey: string): Record<string, boolean> {
    if (columnKey === this.rowRemoveActionColumn) {
      return {
        'row-action-column': true
      };
    }

    const frozen = this.isFrozenColumn(columnKey);
    return {
      'compact-column': this.isCompactColumn(columnKey),
      'wide-column': this.isWideColumn(columnKey),
      'numeric-column': this.isNumericColumn(columnKey),
      'frozen-column': frozen,
      'non-frozen-column': !frozen
    };
  }

  public getBodyCellClasses(columnKey: string, row: RowData): Record<string, boolean> {
    if (columnKey === this.rowRemoveActionColumn) {
      return {
        'row-action-column': true
      };
    }

    const frozen = this.isFrozenColumn(columnKey);
    return {
      'compact-column': this.isCompactColumn(columnKey),
      'wide-column': this.isWideColumn(columnKey),
      'numeric-column': this.isNumericColumn(columnKey, row),
      'dropdown-column': this.isDropdownColumn(columnKey),
      'frozen-column': frozen,
      'non-frozen-column': !frozen
    };
  }

  isFrozenColumn(columnKey: string): boolean {
    const orderedColumns = Array.isArray(this.displayedColumns) ? this.displayedColumns : [];
    const targetIndex = orderedColumns.indexOf(columnKey);
    if (targetIndex < 0) {
      return this.isColumnConfiguredFrozen(columnKey);
    }

    // Excel-like freeze pane behavior: only a left contiguous block is frozen.
    for (let idx = 0; idx <= targetIndex; idx += 1) {
      if (!this.isColumnConfiguredFrozen(orderedColumns[idx])) {
        return false;
      }
    }

    return true;
  }

  private isColumnConfiguredFrozen(columnKey: string): boolean {
    const config = this.columnConfigMap[columnKey] || this.dropdownColumnConfig[columnKey];
    if (!config) {
      return false;
    }

    const rawValue = (config as any).freeze ?? (config as any).frozen ?? (config as any).sticky;
    return this.parseBooleanFlag(rawValue) === true;
  }

  public formatHeaderLabel(value: any): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '';
    }

    const normalized = raw
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    return normalized
      .split(' ')
      .map((word) => {
        if (!word) {
          return word;
        }

        const leading = (word.match(/^[^A-Za-z0-9]*/) || [''])[0];
        const trailing = (word.match(/[^A-Za-z0-9]*$/) || [''])[0];
        const core = word.slice(leading.length, word.length - trailing.length);
        if (!core) {
          return word;
        }

        if (/^[A-Z0-9]{1,3}$/.test(core)) {
          return `${leading}${core}${trailing}`;
        }

        const lowerCore = core.toLowerCase();
        const titleCore = lowerCore.charAt(0).toUpperCase() + lowerCore.slice(1);
        return `${leading}${titleCore}${trailing}`;
      })
      .join(' ');
  }

  getDropdownOptions(row: RowData, columnKey: string): string[] {
    const columnConfig = this.getCellConfig(row, columnKey);
    if (!columnConfig) {
      return [];
    }

    const rowOptionsField = columnConfig.optionsField || `${columnKey}Options`;
    const rowOptions = rowOptionsField && Array.isArray(row?.[rowOptionsField]) ? row[rowOptionsField] : [];
    const columnOptions = Array.isArray(columnConfig.options) ? columnConfig.options : [];

    const mergedOptions = [...rowOptions, ...columnOptions]
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0);

    return Array.from(new Set(mergedOptions));
  }

  onDropdownChange(row: RowData, columnKey: string, value: any): void {
    if (!this.isColumnEditable(columnKey, row)) {
      return;
    }

    row[columnKey] = this.normalizeValueForColumn(columnKey, value);
    this.syncWorkflowCtxForColumn(columnKey, row[columnKey]);
    this.markRowAsDirty(row);
    this.publishWidgetDataIfEnabled();
  }

  onTextInputChange(row: RowData, columnKey: string, value: any): void {
    if (!this.isColumnEditable(columnKey, row)) {
      return;
    }

    if (this.isNumericColumn(columnKey, row)) {
      const normalized = this.normalizeNumericForStorage(value);
      row[columnKey] = normalized;
      this.syncWorkflowCtxForColumn(columnKey, normalized);
      this.markRowAsDirty(row);
      this.publishWidgetDataIfEnabled();
      return;
    }

    row[columnKey] = value ?? '';
    this.syncWorkflowCtxForColumn(columnKey, row[columnKey]);
    this.markRowAsDirty(row);
    this.publishWidgetDataIfEnabled();
  }

  getInputDisplayValue(columnKey: string, value: any, row?: RowData): any {
    if (!this.isNumericColumn(columnKey, row)) {
      return value ?? '';
    }

    return this.formatNumericForDisplay(value);
  }

  getCellDisplayValue(columnKey: string, value: any, row?: RowData): any {
    const mappedDisplayValue = this.resolveMappedDisplayValue(columnKey, value);
    if (mappedDisplayValue !== value) {
      return mappedDisplayValue ?? '';
    }

    if (!this.isNumericColumn(columnKey, row)) {
      return value ?? '';
    }

    return this.formatNumericForDisplay(value);
  }

  private resolveMappedDisplayValue(columnKey: string, value: any): any {
    const config = this.columnConfigMap[columnKey] || this.dropdownColumnConfig[columnKey];
    if (!config || typeof config !== 'object') {
      return value;
    }

    const map = (config as any).valueDisplayMap || (config as any).displayValueMap || (config as any).valueMap;
    if (!map || typeof map !== 'object' || Array.isArray(map)) {
      return value;
    }

    const raw = value === undefined || value === null ? '' : String(value).trim();
    if (raw.length === 0) {
      return value;
    }

    if (Object.prototype.hasOwnProperty.call(map, raw)) {
      return (map as Record<string, any>)[raw];
    }

    const loweredRaw = raw.toLowerCase();
    const matchedKey = Object.keys(map).find((key) => String(key).trim().toLowerCase() === loweredRaw);
    if (matchedKey !== undefined) {
      return (map as Record<string, any>)[matchedKey];
    }

    return value;
  }

  private initializeTable(): void {
    this.filterSubscription?.unsubscribe();
    this.widgetDataSubscription?.unsubscribe();
    this.resetStateSubscription?.unsubscribe();
    this.apiDataSubscription?.unsubscribe();
    this.widgetRefreshSubscription?.unsubscribe();
    this.selectedRowsByValue.clear();
    this.lastFilterSelectionSignature = '';
    this.enabledEditableColumns.clear();
    this.dirtyRowIds.clear();
    this.deletingRowIds.clear();
    this.selectedDeleteRowIds.clear();
    this.rowIdSequence = 0;
    this.loadingButtons.clear();
    this.cellConfigField = String(this.data?.cellConfigField || '__cellConfig').trim() || '__cellConfig';

    const headerColumns: HeaderColumnConfig[] = this.data?.headerColumns || [];
    const dropdownColumns: HeaderColumnConfig[] = this.data?.dropdownColumns || [];
    const normalizedHeaderColumns = this.normalizeHeaderColumns(headerColumns);
    const normalizedDropdownColumns = this.normalizeHeaderColumns(dropdownColumns);

    this.displayedColumns = normalizedHeaderColumns.map((column) => column.key);
    this.columnLabels = Object.fromEntries(
      normalizedHeaderColumns.map((column) => [column.key, column.label])
    );
    this.columnConfigMap = Object.fromEntries(
      normalizedHeaderColumns.map((column) => [column.key, { ...column.config }])
    );

    const dropdownMap: Record<string, HeaderColumnConfig> = {};

    normalizedHeaderColumns.forEach((column) => {
      if (column.config.type === 'dropdown' || column.config.isDropdown) {
        dropdownMap[column.key] = { ...column.config };
      }
    });

    normalizedDropdownColumns.forEach((column) => {
      dropdownMap[column.key] = {
        ...(dropdownMap[column.key] || {}),
        ...column.config,
        type: 'dropdown'
      };
    });

    const sourceRows: RowData[] = this.data?.tableData || this.data?.rows || [];
    this.baseDataSource = sourceRows.map((row) => this.buildDisplayRow(row));
    this.dataSource = this.baseDataSource.map((row) => ({ ...row }));
    this.dropdownColumnConfig = dropdownMap;
    this.normalizeDropdownValuesForRows(this.dataSource);
    this.prefillRowsFromWorkflowCtx(this.dataSource);

    const restoredFromExternalWidgetData = this.bindToExternalWidgetData();
    if (!restoredFromExternalWidgetData) {
      this.publishWidgetDataIfEnabled();
    }

    this.buildMappingRowsByValue();
    this.dynamicFilterKey = this.data?.filterKey || this.data?.discountTypeFilterKey || '';
    this.selectionMode = this.data?.selectionMode === 'append' ? 'append' : 'replace';
    this.initialLoadPayloadOverride = this.resolveInitialLoadPayloadOverride();
    const hasSelectionMappings = Object.keys(this.mappingRowsByValue).length > 0;
    const shouldLoadApiOnFilterChange = this.shouldLoadApiOnFilterChange();

    this.resetStateSubscription = this.globalFilterService.resetState$.subscribe(() => {
      this.resetTableState();
    });

    if (this.dynamicFilterKey && (hasSelectionMappings || shouldLoadApiOnFilterChange)) {
      this.filterSubscription = this.globalFilterService.filter$.subscribe((filters) => {
        const selectedValues = this.extractSelectedValues(filters, this.dynamicFilterKey);
        const nextSelectionSignature = JSON.stringify(selectedValues);
        const selectionChanged = nextSelectionSignature !== this.lastFilterSelectionSignature;
        const changedFilterKey = String(filters?.changedFilterKey || '').trim();
        const changedTargetFilter = changedFilterKey.length > 0 && changedFilterKey === this.dynamicFilterKey;
        const shouldHandleChange = changedTargetFilter || (shouldLoadApiOnFilterChange && selectionChanged);

        if (!shouldHandleChange) {
          return;
        }
        this.lastFilterSelectionSignature = nextSelectionSignature;

        if (hasSelectionMappings) {
          if (!this.shouldRetainRowsOnEmptySelection(selectedValues)) {
            this.applySelections(selectedValues);
          }
        }

        if (shouldLoadApiOnFilterChange) {
          this.loadRowsFromFilterApi(selectedValues);
        }
      });

      const currentFilters = this.globalFilterService.getCurrentFilters();
      const currentSelections = this.extractSelectedValues(currentFilters, this.dynamicFilterKey);
      this.lastFilterSelectionSignature = JSON.stringify(currentSelections);
      if (!restoredFromExternalWidgetData && hasSelectionMappings) {
        this.applySelections(currentSelections);
      }
      if (shouldLoadApiOnFilterChange && (this.data?.callApiOnInit === true || !restoredFromExternalWidgetData)) {
        this.loadRowsFromFilterApi(currentSelections, this.data?.callApiOnInit === true, false, true);
      }
    }

    const hasApiUrlForRefresh = String(this.data?.apiUrl || '').trim().length > 0;
    if (this.data?.widgetId && hasApiUrlForRefresh) {
      this.widgetRefreshSubscription = this.globalFilterService.widgetRefresh$.subscribe((refreshWidgetId) => {
        if (refreshWidgetId === this.data.widgetId || refreshWidgetId === '*') {
          const currentFilters = this.globalFilterService.getCurrentFilters();
          const currentSelections = this.extractSelectedValues(currentFilters, this.dynamicFilterKey);
          this.loadRowsFromFilterApi(currentSelections, true, true); // allow empty selection + explicit refresh
        }
      });
    }
  }

  private normalizeValueForColumn(columnKey: string, value: any): any {
    const isMultiSelectColumn = this.isMultiSelect(columnKey);

    if (isMultiSelectColumn) {
      if (Array.isArray(value)) {
        return value
          .map((item) => String(item).trim())
          .filter((item) => item.length > 0);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      }

      return [];
    }

    if (Array.isArray(value)) {
      return value[0] ?? '';
    }

    return value ?? '';
  }

  private resolveAddRowButtonConfig(): AddRowButtonConfig {
    const rawConfig = this.data?.addRowButton ?? this.data?.manualRowButton ?? this.data?.addRow;
    if (rawConfig === true) {
      return { enabled: true };
    }

    if (typeof rawConfig === 'string') {
      const label = rawConfig.trim();
      return {
        enabled: true,
        ...(label ? { label } : {})
      };
    }

    if (rawConfig && typeof rawConfig === 'object' && !Array.isArray(rawConfig)) {
      return rawConfig as AddRowButtonConfig;
    }

    return {};
  }

  private resolveFooterButtonConfigs(): FooterButtonConfig[] {
    const rawConfig = this.data?.footerButtons ?? this.data?.footerButton ?? this.data?.actionButton ?? this.data?.saveButton;
    if (rawConfig === true) {
      return [{ enabled: true, label: 'Save' }];
    }

    if (typeof rawConfig === 'string') {
      const label = rawConfig.trim();
      return [{
        enabled: true,
        ...(label ? { label } : {})
      }];
    }

    if (rawConfig && typeof rawConfig === 'object') {
      if (Array.isArray(rawConfig)) {
        return rawConfig as FooterButtonConfig[];
      }

      return [rawConfig as FooterButtonConfig];
    }

    return [];
  }

  private shouldRenderFooterButton(config: FooterButtonConfig): boolean {
    if (!config || typeof config !== 'object') {
      return false;
    }

    const explicit = this.parseBooleanFlag(config.enabled);
    if (explicit === false) {
      return false;
    }

    if (explicit === true) {
      return true;
    }

    return !!String(config.label || '').trim();
  }

  isFooterButtonDisabled(config: FooterButtonConfig): boolean {
    const explicit = this.parseBooleanFlag(config.enabled);
    if (explicit === false) {
      return true;
    }

    const saveDirtyRowsOnly = this.parseBooleanFlag(config.saveDirtyRowsOnly) !== false;
    if (!saveDirtyRowsOnly) {
      return false;
    }

    return this.getRowsToSave(this.resolveFooterSubmitApiConfigs(config)).length === 0;
  }

  private resolveFooterSubmitApiConfigs(config: FooterButtonConfig): FooterSubmitApiConfig[] {
    const submitApi = config.submitApi;
    const nested = submitApi;
    if (!nested) {
      const fallback = this.normalizeFooterSubmitApiConfig(config);
      return fallback ? [fallback] : [];
    }

    if (Array.isArray(nested)) {
      return nested
        .map((api) => this.normalizeFooterSubmitApiConfig(api))
        .filter((api): api is FooterSubmitApiConfig => !!api);
    }

    const normalized = this.normalizeFooterSubmitApiConfig(nested);
    return normalized ? [normalized] : [];
  }

  private normalizeFooterSubmitApiConfig(source: any): FooterSubmitApiConfig | null {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return null;
    }

    const apiUrl = String(source.apiUrl || source.url || '').trim();
    if (!apiUrl) {
      return null;
    }

    return {
      ...(source as FooterSubmitApiConfig),
      apiUrl
    };
  }

  private resolveRowDeleteButtonConfig(): RowDeleteButtonConfig {
    const rawConfig = this.data?.rowDeleteButton ?? this.data?.deleteRowButton ?? this.data?.deleteButton;
    if (rawConfig === true) {
      return { enabled: true };
    }

    if (typeof rawConfig === 'string') {
      const label = rawConfig.trim();
      return {
        enabled: true,
        ...(label ? { label } : {})
      };
    }

    if (rawConfig && typeof rawConfig === 'object' && !Array.isArray(rawConfig)) {
      return rawConfig as RowDeleteButtonConfig;
    }

    return {};
  }

  private normalizeRowDeleteSubmitApiConfig(config: RowDeleteButtonConfig): FooterSubmitApiConfig | null {
    const source = config.submitApi && typeof config.submitApi === 'object'
      ? config.submitApi
      : config;
    return this.normalizeFooterSubmitApiConfig(source);
  }

  private buildFooterSubmitRequests(configs: FooterSubmitApiConfig[]): any[] {
    const rows = this.getRowsToSave(configs);
    return configs.flatMap((config) => rows.map((row) => {
      const payload = this.buildFooterSubmitPayload(config, row);
      const request = this.buildFooterSubmitRequest(config, payload);
      return request;
    }).filter((request) => !!request));
  }

  private getRowsToSave(configs: FooterSubmitApiConfig[]): RowData[] {
    const saveDirtyRowsOnly = configs.some((config) => this.parseBooleanFlag(config.saveDirtyRowsOnly) !== false);
    const rows = Array.isArray(this.dataSource) ? this.dataSource : [];
    if (!saveDirtyRowsOnly) {
      return rows.filter((row) => !!row && typeof row === 'object');
    }

    return rows.filter((row) => !!row && typeof row === 'object' && this.isRowDirty(row));
  }

  private buildFooterSubmitPayload(config: FooterSubmitApiConfig, row: RowData): Record<string, any> {
    const basePayload = this.toRecord(this.deepClone(config.payload ?? {}));
    const requestDataKey = String(config.requestDataKey || 'requestData').trim() || 'requestData';
    const requestData = this.toRecord(basePayload[requestDataKey]);
    const includeColumns = this.toStringArray(config.includeColumns);
    const includeKeys = includeColumns.length > 0 ? includeColumns : this.displayedColumns;
    const excludeKeySet = new Set(this.toStringArray(config.excludeColumns).map((key) => this.normalizeKey(key)));
    const rowFieldMap = this.toRecord(config.rowFieldMap);

    includeKeys.forEach((columnKey) => {
      const normalizedKey = this.normalizeKey(columnKey);
      if (!columnKey || excludeKeySet.has(normalizedKey)) {
        return;
      }
      if (row[columnKey] !== undefined) {
        requestData[columnKey] = row[columnKey];
      }
    });

    Object.entries(rowFieldMap).forEach(([targetKey, sourceKey]) => {
      const target = String(targetKey || '').trim();
      const source = String(sourceKey || '').trim();
      if (!target || !source) {
        return;
      }
      if (excludeKeySet.has(this.normalizeKey(target))) {
        return;
      }
      if (row[source] !== undefined) {
        requestData[target] = row[source];
      }
    });

    basePayload[requestDataKey] = requestData;
    return basePayload;
  }

  private buildFooterSubmitRequest(
    config: FooterSubmitApiConfig,
    payload: Record<string, any>
  ): any {
    const apiUrl = this.resolveApiUrl(String(config.apiUrl || config.url || '').trim());
    const method = String(config.method || 'POST').trim().toUpperCase();
    if (!apiUrl || method !== 'POST') {
      return null;
    }

    return this.http.post(apiUrl, payload);
  }

  private resolveApiUrl(apiUrl?: string): string {
    const configured = String(apiUrl || '').trim();
    if (!configured) {
      return '';
    }

    return configured.replace('${API_BASE_URL}', environment.API_BASE_URL);
  }

  protected toRecord(value: any): Record<string, any> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return { ...value };
    }
    return {};
  }

  protected deepClone<T>(value: T): T {
    if (value === undefined || value === null) {
      return value;
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }

  private parseBooleanFlag(value: any): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
        return true;
      }
      if (normalized === 'false' || normalized === '0' || normalized === 'no') {
        return false;
      }
    }

    return null;
  }

  private isAutoTableHeightEnabled(): boolean {
    const explicit = this.parseBooleanFlag(
      this.data?.tableAutoHeight ??
      this.data?.autoTableHeight ??
      this.data?.adaptiveTableHeight
    );

    return explicit !== false;
  }

  private resolveEffectiveTableMaxHeightPx(): number {
    const viewportHeight = this.resolveViewportHeightPx();
    const viewportOffset = this.toPositiveNumber(
      this.data?.tableViewportOffsetPx ??
      this.data?.tableHeightOffsetPx ??
      this.data?.viewportOffsetPx
    ) ?? this.defaultTableViewportOffsetPx;
    const configuredMax = this.toPositiveNumber(
      this.data?.tableMaxHeightPx ??
      this.data?.maxTableHeightPx
    ) ?? this.defaultTableMaxHeightPx;
    const minHeight = this.resolveConfiguredTableMinHeightPx();
    const viewportBoundMax = Math.max(minHeight, viewportHeight - viewportOffset);
    return Math.max(minHeight, Math.min(configuredMax, viewportBoundMax));
  }

  private resolveConfiguredTableMinHeightPx(): number {
    return this.toPositiveNumber(this.data?.tableMinHeightPx ?? this.data?.minTableHeightPx)
      ?? this.defaultTableMinHeightPx;
  }

  private resolveViewportHeightPx(): number {
    if (typeof window !== 'undefined' && Number.isFinite(window.innerHeight)) {
      return window.innerHeight;
    }
    return 900;
  }

  private toPositiveNumber(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }
    return numericValue;
  }

  private shouldInsertManualRowAtStart(config: AddRowButtonConfig): boolean {
    const insertAt = String(config.insertAt ?? this.data?.addRowInsertAt ?? '').trim().toLowerCase();
    return insertAt === 'start' || insertAt === 'top';
  }

  private resolveManualRowDefaults(config: AddRowButtonConfig): RowData {
    const defaults = config.defaults ?? config.row ?? config.rowTemplate ?? this.data?.addRowDefaults;
    if (defaults && typeof defaults === 'object' && !Array.isArray(defaults)) {
      return { ...(defaults as RowData) };
    }
    return {};
  }

  private createManualRow(config: AddRowButtonConfig): RowData {
    const row = this.buildDisplayRow(this.resolveManualRowDefaults(config));

    this.displayedColumns.forEach((columnKey) => {
      const currentValue = row[columnKey];
      if (this.isDropdownColumn(columnKey, row)) {
        row[columnKey] = this.normalizeValueForColumn(columnKey, currentValue);
        return;
      }

      if (this.isNumericColumn(columnKey, row)) {
        row[columnKey] = this.normalizeNumericForStorage(currentValue);
        return;
      }

      row[columnKey] = currentValue ?? '';
    });

    this.ensureInternalRowId(row);
    return row;
  }

  private isLikelyNumericColumn(columnKey: string): boolean {
    const normalizedKey = String(columnKey || '').trim().toLowerCase();
    const normalizedLabel = String(this.columnLabels?.[columnKey] || '')
      .trim()
      .toLowerCase();

    if (!normalizedKey && !normalizedLabel) {
      return false;
    }

    if (this.nonNumericColumnHints.some((hint) => normalizedKey.includes(hint) || normalizedLabel.includes(hint))) {
      return false;
    }

    if (normalizedLabel.includes('%') || normalizedKey.includes('%')) {
      return true;
    }

    return this.numericColumnHints.some((hint) => normalizedKey.includes(hint) || normalizedLabel.includes(hint));
  }

  private isNumericValue(value: any): boolean {
    if (typeof value === 'number') {
      return !Number.isNaN(value);
    }

    if (typeof value !== 'string') {
      return false;
    }

    const cleaned = value.replace(/[%,]/g, '').trim();
    if (!cleaned) {
      return false;
    }

    return !Number.isNaN(Number(cleaned));
  }

  private normalizeNumericForStorage(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }

    const raw = String(value).replace(/,/g, '').trim();
    if (!raw) {
      return '';
    }

    if (raw.endsWith('%')) {
      return raw;
    }

    const sign = raw.startsWith('-') ? '-' : '';
    const unsigned = sign ? raw.slice(1) : raw;
    const parts = unsigned.split('.');
    const integerPart = (parts[0] || '').replace(/\D/g, '');
    const fractionalPart = parts.length > 1 ? parts.slice(1).join('').replace(/\D/g, '') : '';

    if (!integerPart && !fractionalPart) {
      return '';
    }

    if (fractionalPart.length > 0) {
      return `${sign}${integerPart || '0'}.${fractionalPart}`;
    }

    return `${sign}${integerPart}`;
  }

  private formatNumericForDisplay(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }

    const raw = String(value).trim();
    if (!raw) {
      return '';
    }

    if (raw.endsWith('%')) {
      return raw;
    }

    const normalized = this.normalizeNumericForStorage(raw);
    if (!normalized) {
      return raw;
    }

    const sign = normalized.startsWith('-') ? '-' : '';
    const unsigned = sign ? normalized.slice(1) : normalized;
    const parts = unsigned.split('.');
    const integerPart = parts[0] || '0';
    const fractionalPart = parts.length > 1 ? parts[1] : '';
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (fractionalPart.length > 0) {
      return `${sign}${groupedInteger}.${fractionalPart}`;
    }

    return `${sign}${groupedInteger}`;
  }

  private normalizeDropdownValuesForRows(rows: RowData[]): void {
    rows.forEach((row) => {
      Object.keys(this.dropdownColumnConfig).forEach((columnKey) => {
        row[columnKey] = this.normalizeValueForColumn(columnKey, row[columnKey]);
      });
    });
  }

  private buildMappingRowsByValue(): void {
    this.mappingRowsByValue = {};

    const genericMappings = Array.isArray(this.data?.selectionMappings)
      ? (this.data.selectionMappings as SelectionMappingConfig[])
      : [];

    if (genericMappings.length > 0) {
      genericMappings.forEach((mapping) => {
        const parsed = this.parseSelectionMappingRows(mapping);
        if (!parsed) {
          return;
        }
        this.mappingRowsByValue[this.normalizeKey(parsed.triggerValue)] = parsed.rows;
      });
      return;
    }

    this.buildLegacyDiscountMappings();
  }

  private buildLegacyDiscountMappings(): void {
    const rawMappings = this.data?.discountTypeMappings;

    if (Array.isArray(rawMappings)) {
      rawMappings.forEach((mapping: any) => {
        const parsed = this.parseLegacyDiscountMapping(mapping);
        if (!parsed) {
          return;
        }
        const triggerKey = this.normalizeKey(parsed.discountType);
        this.mappingRowsByValue[triggerKey] = [this.createRowFromLegacyMapping(parsed)];
      });
      return;
    }

    if (rawMappings && typeof rawMappings === 'object') {
      Object.entries(rawMappings).forEach(([discountType, mapping]) => {
        const parsed = this.parseLegacyDiscountMapping({ ...(mapping as any), discountType });
        if (!parsed) {
          return;
        }
        const triggerKey = this.normalizeKey(parsed.discountType);
        this.mappingRowsByValue[triggerKey] = [this.createRowFromLegacyMapping(parsed)];
      });
    }
  }

  private parseSelectionMappingRows(
    mapping: SelectionMappingConfig
  ): { triggerValue: string; rows: RowData[] } | null {
    if (!mapping || typeof mapping !== 'object') {
      return null;
    }

    const triggerValue = String(
      mapping.value ?? mapping.triggerValue ?? mapping.selectionValue ?? ''
    ).trim();

    if (!triggerValue) {
      return null;
    }

    let rows: RowData[] = [];
    if (Array.isArray(mapping.rows)) {
      rows = mapping.rows
        .filter((row) => row && typeof row === 'object')
        .map((row) => ({ ...row }));
    } else if (mapping.row && typeof mapping.row === 'object') {
      rows = [{ ...mapping.row }];
    } else {
      const { value, triggerValue: _triggerValue, selectionValue, row, rows: _rows, ...rest } =
        mapping as any;
      if (Object.keys(rest).length > 0) {
        rows = [{ ...rest }];
      }
    }

    if (rows.length === 0) {
      return null;
    }

    return {
      triggerValue,
      rows: rows.map((row) => this.buildDisplayRow(row))
    };
  }

  private parseLegacyDiscountMapping(mapping: any): LegacyDiscountMappingConfig | null {
    if (!mapping) {
      return null;
    }

    const discountType = String(mapping.discountType || mapping.discount_type || '').trim();
    if (!discountType) {
      return null;
    }

    return {
      discountType,
      quoteLevelDiscount: String(
        mapping.quoteLevelDiscount ??
        mapping.quote_level_discount ??
        ''
      ).trim(),
      reasons: this.toStringArray(mapping.reasons || mapping.reasonOptions || mapping.options),
      defaultSelectedReason: mapping.defaultSelectedReason,
      defaultSelectedReasons: this.toStringArray(mapping.defaultSelectedReasons)
    };
  }

  private toStringArray(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [];
  }

  private extractSelectedValues(filters: any, filterKey: string): string[] {
    const rawValue = filters?.requestParams?.[filterKey] ?? filters?.queryParams?.[filterKey];

    if (Array.isArray(rawValue)) {
      const flattened = rawValue.flatMap((value) => (Array.isArray(value) ? value : [value]));
      return flattened
        .map((value) => (value === undefined || value === null ? '' : String(value).trim()))
        .filter((value) => value.length > 0);
    }

    const singleValue = rawValue === undefined || rawValue === null ? '' : String(rawValue).trim();
    return singleValue ? [singleValue] : [];
  }

  private applySelections(selectedValues: string[]): void {
    const normalizedSelectedKeys = Array.from(
      new Set(
        selectedValues
          .map((value) => this.normalizeKey(value))
          .filter((value) => value.length > 0)
      )
    );

    if (this.selectionMode === 'replace') {
      this.selectedRowsByValue.clear();
      normalizedSelectedKeys.forEach((normalizedKey) => {
        const rows = this.mappingRowsByValue[normalizedKey];
        if (rows?.length) {
          this.selectedRowsByValue.set(normalizedKey, this.cloneRows(rows));
        }
      });
      this.rebuildTableData();
      return;
    }

    const selectedSet = new Set(normalizedSelectedKeys);
    Array.from(this.selectedRowsByValue.keys()).forEach((key) => {
      if (!selectedSet.has(key)) {
        this.selectedRowsByValue.delete(key);
      }
    });

    normalizedSelectedKeys.forEach((normalizedKey) => {
      const rows = this.mappingRowsByValue[normalizedKey];
      if (rows?.length) {
        this.selectedRowsByValue.set(normalizedKey, this.cloneRows(rows));
      }
    });

    this.rebuildTableData();
  }

  private rebuildTableData(): void {
    const baseRows = this.baseDataSource.map((row) => ({ ...row }));
    const mappedRows = Array.from(this.selectedRowsByValue.values())
      .flat()
      .map((row) => ({ ...row }));

    this.dataSource = this.dedupeRows(this.normalizeRowsForDisplay([...baseRows, ...mappedRows]));
    this.normalizeDropdownValuesForRows(this.dataSource);
    this.publishWidgetDataIfEnabled();
  }

  private createRowFromLegacyMapping(mapping: LegacyDiscountMappingConfig): RowData {
    const discountTypeColumnKey = this.data?.discountTypeColumnKey || 'discount_type';
    const reasonColumnKey = this.data?.reasonColumnKey || 'reason';
    const quoteLevelDiscountColumnKey =
      this.data?.quoteLevelDiscountColumnKey || 'quote_level_discount';
    const reasonOptionsField =
      this.dropdownColumnConfig?.[reasonColumnKey]?.optionsField || `${reasonColumnKey}Options`;

    const reasonValue = this.isMultiSelect(reasonColumnKey)
      ? (mapping.defaultSelectedReasons?.length ? mapping.defaultSelectedReasons : mapping.reasons)
      : (mapping.defaultSelectedReason || mapping.reasons[0] || '');

    const row: RowData = {
      [discountTypeColumnKey]: mapping.discountType,
      [reasonColumnKey]: reasonValue,
      [quoteLevelDiscountColumnKey]: mapping.quoteLevelDiscount,
      [reasonOptionsField]: mapping.reasons
    };

    return this.buildDisplayRow(row);
  }

  private buildDisplayRow(row: RowData): RowData {
    const displayRow: RowData = { ...row };
    this.displayedColumns.forEach((columnKey) => {
      if (displayRow[columnKey] === undefined || displayRow[columnKey] === null) {
        displayRow[columnKey] = '';
      }
    });
    this.ensureInternalRowId(displayRow);
    return displayRow;
  }

  private normalizeRowsForDisplay(rows: RowData[]): RowData[] {
    return Array.isArray(rows) ? rows.map((row) => this.buildDisplayRow(row)) : [];
  }

  private normalizeHeaderColumns(columns: HeaderColumnConfig[]): NormalizedHeaderColumnConfig[] {
    const seenKeys = new Set<string>();

    return (Array.isArray(columns) ? columns : [])
      .filter((column) => column && typeof column === 'object')
      .map((column, index) => {
        const originalKey = String(column.key ?? '').trim();
        const label = String(column.label ?? originalKey).trim();
        let resolvedKey = originalKey;

        if (!resolvedKey || seenKeys.has(resolvedKey)) {
          resolvedKey = `__table_col_${index + 1}`;
        }

        while (seenKeys.has(resolvedKey)) {
          resolvedKey = `${resolvedKey}_${index + 1}`;
        }

        seenKeys.add(resolvedKey);

        return {
          key: resolvedKey,
          label,
          config: { ...column, key: resolvedKey }
        };
      });
  }

  private clearDirtyRows(): void {
    this.dirtyRowIds.clear();
  }

  private markRowAsDirty(row: RowData): void {
    const rowId = this.ensureInternalRowId(row);
    if (!rowId) {
      return;
    }

    this.dirtyRowIds.add(rowId);
  }

  private isRowDirty(row: RowData): boolean {
    const rowId = this.ensureInternalRowId(row);
    if (!rowId) {
      return false;
    }

    return this.dirtyRowIds.has(rowId);
  }

  private getInternalRowId(row: RowData): string {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return '';
    }

    return String(row[this.internalRowIdField] ?? '').trim();
  }

  private ensureInternalRowId(row: RowData): string {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return '';
    }

    const existing = this.getInternalRowId(row);
    if (existing) {
      return existing;
    }

    this.rowIdSequence += 1;
    const nextId = `row-${Date.now()}-${this.rowIdSequence}`;
    row[this.internalRowIdField] = nextId;
    return nextId;
  }

  getCellConfig(row: RowData | undefined, columnKey: string): HeaderColumnConfig {
    const base = { ...(this.columnConfigMap[columnKey] || this.dropdownColumnConfig[columnKey] || { key: columnKey }) };
    if (!row || typeof row !== 'object') {
      return base;
    }

    const scoped = row?.[this.cellConfigField];
    const scopedColumn = scoped && typeof scoped === 'object' && !Array.isArray(scoped)
      ? (scoped as Record<string, any>)[columnKey]
      : undefined;
    const direct = row?.[`${columnKey}Config`];
    const overlay = scopedColumn || direct;

    if (overlay && typeof overlay === 'object' && !Array.isArray(overlay)) {
      return { ...base, ...overlay, key: columnKey };
    }

    return base;
  }

  private cloneRows(rows: RowData[]): RowData[] {
    return rows.map((row) => ({ ...row }));
  }

  private normalizeKey(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private shouldLoadApiOnFilterChange(): boolean {
    const apiUrl = String(this.data?.apiUrl || '').trim();
    const enabled = this.data?.loadApiOnFilterSelection !== false;
    return !!apiUrl && !!this.dynamicFilterKey && enabled;
  }

  private loadRowsFromFilterApi(
    selectedValues: string[],
    forceCall = false,
    bypassDedupe = false,
    isInitCall = false
  ): void {
    const values = Array.isArray(selectedValues)
      ? selectedValues
        .map((value) => String(value || '').trim())
        .filter((value) => value.length > 0)
      : [];
    const shouldCallOnEmpty = this.data?.callApiOnEmptySelection === true || forceCall;
    if (!values.length && !shouldCallOnEmpty) {
      if (this.data?.clearRowsOnEmptySelection === true) {
        this.baseDataSource = [];
        this.dataSource = [];
        this.publishWidgetDataIfEnabled();
      }
      return;
    }

    const apiUrl = String(this.data?.apiUrl || '').trim();
    if (!apiUrl) {
      return;
    }

    const includeSelectionKey = values.length > 0 || this.data?.includeEmptySelectionPayloadKey === true;
    const payloadKey = String(this.data?.apiPayloadKey || this.dynamicFilterKey || '').trim();
    if (!payloadKey && includeSelectionKey) {
      return;
    }
    const payload = this.buildFilterApiPayload(
      payloadKey,
      values,
      includeSelectionKey && !!payloadKey,
      isInitCall
    );
    const method = String(this.data?.method || 'POST').trim().toUpperCase();
    const requestSignature = `${method}|${apiUrl}|${JSON.stringify(payload)}`;
    const dedupeApiRequests = this.data?.dedupeApiRequests !== false;
    if (dedupeApiRequests && !bypassDedupe) {
      if (
        requestSignature === this.inFlightApiRequestSignature ||
        requestSignature === this.lastApiRequestSignature
      ) {
        return;
      }
    }

    const request$ = method === 'GET'
      ? this.http.get(apiUrl, { params: this.buildHttpParams(payload) })
      : this.http.post(apiUrl, payload);

    this.apiDataSubscription?.unsubscribe();
    this.inFlightApiRequestSignature = requestSignature;
    this.lastApiRequestSignature = requestSignature;
    this.apiDataSubscription = request$.subscribe({
      next: (response: any) => {
        this.inFlightApiRequestSignature = '';
        const rows = this.extractRowsFromApiResponse(response);
        if (!rows.length && this.data?.retainExistingOnEmpty === true) {
          return;
        }

        this.baseDataSource = rows.map((row) => this.buildDisplayRow(row));
        this.dataSource = this.dedupeRows(rows.map((row) => this.buildDisplayRow(row)));
        this.normalizeDropdownValuesForRows(this.dataSource);
        this.prefillRowsFromWorkflowCtx(this.dataSource);
        this.publishWidgetDataIfEnabled();
      },
      error: (error: any) => {
        this.inFlightApiRequestSignature = '';
        this.lastApiRequestSignature = '';
        console.error('[table-w-dropdown-2] Failed to load API rows for filter selection', {
          apiUrl,
          payload,
          error
        });
      }
    });
  }

  private buildFilterApiPayload(
    payloadKey: string,
    selectedValues: string[],
    includeSelectionKey = true,
    isInitCall = false
  ): Record<string, any> {
    const overridePayload =
      isInitCall && selectedValues.length === 0 ? this.initialLoadPayloadOverride : null;
    const basePayload = overridePayload ?? this.data?.payload;
    const payload =
      basePayload && typeof basePayload === 'object' && !Array.isArray(basePayload)
        ? { ...(basePayload as Record<string, any>) }
        : {};

    const removeKeysOnSelectionRaw = Array.isArray(this.data?.removePayloadKeysOnSelection)
      ? this.data.removePayloadKeysOnSelection
      : [];
    const removeKeysOnSelection = removeKeysOnSelectionRaw
      .map((item: any) => String(item || '').trim())
      .filter((item: string) => item.length > 0);
    if (selectedValues.length > 0 && removeKeysOnSelection.length > 0) {
      removeKeysOnSelection.forEach((keyToRemove: string) => {
        delete payload[keyToRemove];
      });
    }

    if (!includeSelectionKey || !payloadKey) {
      return payload;
    }
    const shouldNestSelectedValues =
      this.data?.wrapFilterSelectionInNestedArray === true ||
      this.data?.nestFilterSelectionPayload === true;
    payload[payloadKey] = shouldNestSelectedValues ? [selectedValues] : selectedValues;
    return payload;
  }

  private resolveInitialLoadPayloadOverride(): Record<string, any> | null {
    const config = this.data?.initialLoadVisitCountPayloadToggle;
    if (!config || typeof config !== 'object' || Array.isArray(config)) {
      return null;
    }

    const enabled = this.parseBooleanFlag((config as Record<string, any>)['enabled']);
    if (enabled === false) {
      return null;
    }

    const payload = (config as Record<string, any>)['payload'];
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return null;
    }

    const triggerCount = Number((config as Record<string, any>)['triggerCount'] ?? 2);
    if (!Number.isInteger(triggerCount) || triggerCount < 1) {
      return null;
    }

    const storageKey = this.resolveInitialLoadVisitStorageKey(config as Record<string, any>);
    if (!storageKey) {
      return null;
    }

    const currentCount = this.readInitialLoadVisitCount(storageKey);
    const nextCount = currentCount + 1;
    const forceFromPreviousRoute = this.shouldForceInitialLoadPayloadFromPreviousRoute(config as Record<string, any>);
    const shouldUseOverride = forceFromPreviousRoute || nextCount >= triggerCount;
    const resetCountOnTrigger = this.parseBooleanFlag((config as Record<string, any>)['resetCountOnTrigger']) !== false;

    this.writeInitialLoadVisitCount(
      storageKey,
      shouldUseOverride && resetCountOnTrigger ? 0 : nextCount
    );

    return shouldUseOverride ? { ...(payload as Record<string, any>) } : null;
  }

  private shouldForceInitialLoadPayloadFromPreviousRoute(config: Record<string, any>): boolean {
    const routes = this.toStringArray(config['forcePayloadFromPreviousRoutes']);
    if (routes.length === 0) {
      return false;
    }

    const previousRoute = this.readPreviousRoute();
    if (!previousRoute) {
      return false;
    }

    return routes
      .map((route) => this.normalizeRoutePath(route))
      .filter((route) => route.length > 0)
      .includes(previousRoute);
  }

  private resolveInitialLoadVisitStorageKey(config: Record<string, any>): string {
    const configuredKey = String(config['storageKey'] || '').trim();
    if (configuredKey) {
      return configuredKey;
    }

    const widgetId = String(this.data?.widgetId || '').trim();
    const apiUrl = String(this.data?.apiUrl || '').trim();
    const routePath = this.resolveCurrentPath();
    const segments = [routePath, widgetId, apiUrl].filter((value) => value.length > 0);

    return segments.length > 0
      ? `table-w-dropdown-2:init-visit:${segments.join('|')}`
      : '';
  }

  private resolveCurrentPath(): string {
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }

    return this.normalizeRoutePath(String(window.location.pathname || '').trim());
  }

  private readInitialLoadVisitCount(storageKey: string): number {
    if (typeof sessionStorage === 'undefined') {
      return 0;
    }

    const rawValue = sessionStorage.getItem(storageKey);
    const parsedValue = Number(rawValue);
    return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
  }

  private writeInitialLoadVisitCount(storageKey: string, value: number): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.setItem(storageKey, String(Math.max(0, value)));
  }

  private readPreviousRoute(): string {
    if (typeof sessionStorage === 'undefined') {
      return '';
    }

    return this.normalizeRoutePath(sessionStorage.getItem(this.previousRouteStorageKey) || '');
  }

  private normalizeRoutePath(route: string): string {
    const rawRoute = String(route || '').split('?')[0].split('#')[0].trim();
    if (!rawRoute) {
      return '';
    }

    return rawRoute.startsWith('/') ? rawRoute : `/${rawRoute}`;
  }

  private buildHttpParams(payload: Record<string, any>): HttpParams {
    let params = new HttpParams();
    Object.entries(payload || {}).forEach(([key, rawValue]) => {
      const values = Array.isArray(rawValue) ? rawValue.flat(Infinity) : [rawValue];
      values
        .map((value) => (value === undefined || value === null ? '' : String(value).trim()))
        .filter((value) => value.length > 0)
        .forEach((value) => {
          params = params.append(key, value);
        });
    });
    return params;
  }

  private extractRowsFromApiResponse(response: any): RowData[] {
    if (response === undefined || response === null) {
      return [];
    }

    const responsePath = String(this.data?.responsePath || '').trim();
    const responseKey = String(this.data?.responseKey || '').trim();
    let extracted: any = response;

    if (responsePath) {
      extracted = this.getValueByPath(response, responsePath);
    } else if (responseKey) {
      extracted = response?.[responseKey];
    }

    const rowsFromExtracted = this.collectRowsFromNestedResponse(extracted);
    if (rowsFromExtracted.length > 0) {
      return rowsFromExtracted;
    }

    const rowsFromRoot = this.collectRowsFromNestedResponse(response);
    if (rowsFromRoot.length > 0) {
      return rowsFromRoot;
    }

    return [];
  }

  private collectRowsFromNestedResponse(source: any, depth = 0): RowData[] {
    if (source === undefined || source === null || depth > 8) {
      return [];
    }

    if (Array.isArray(source)) {
      return source.flatMap((entry) => this.collectRowsFromNestedResponse(entry, depth + 1));
    }

    if (typeof source !== 'object') {
      return [];
    }

    const current = source as Record<string, any>;
    const dataValue = current['data'];
    if (dataValue !== undefined && dataValue !== null) {
      return this.collectRowsFromNestedResponse(dataValue, depth + 1);
    }

    const responseList = current['responseList'];
    if (Array.isArray(responseList)) {
      const rowsFromResponseList = this.collectRowsFromNestedResponse(responseList, depth + 1);
      if (rowsFromResponseList.length > 0) {
        return rowsFromResponseList;
      }
    }

    const rowsFromNestedArrays = Object.values(current)
      .filter((value) => Array.isArray(value))
      .flatMap((value) => this.collectRowsFromNestedResponse(value, depth + 1));

    if (rowsFromNestedArrays.length > 0) {
      return this.normalizeRowsForDisplay(rowsFromNestedArrays);
    }

    if (Object.keys(current).length > 0) {
      return [this.buildDisplayRow(current as RowData)];
    }

    return [];
  }

  private getValueByPath(source: any, path: string): any {
    return String(path || '')
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0)
      .reduce((acc, segment) => {
        if (acc === undefined || acc === null) {
          return undefined;
        }

        const arrayMatch = segment.match(/^([^[\]]+)\[(\d+)\]$/);
        if (!arrayMatch) {
          return acc?.[segment];
        }

        const value = acc?.[arrayMatch[1]];
        return Array.isArray(value) ? value[Number(arrayMatch[2])] : undefined;
      }, source);
  }

  private bindToExternalWidgetData(): boolean {
    if (!this.data?.useWidgetData || !this.data?.widgetId) {
      return false;
    }

    let restoredFromWidgetData = false;
    const initialRows = this.globalFilterService.getWidgetData(this.data.widgetId);
    if (initialRows.length > 0) {
      this.updateRowsFromExternalWidgetData(initialRows);
      restoredFromWidgetData = true;
    }

    this.widgetDataSubscription = this.globalFilterService.widgetData$.subscribe((widgetDataMap) => {
      const rows = widgetDataMap?.[this.data.widgetId];
      if (!Array.isArray(rows)) {
        return;
      }

      if (this.suppressSelfWidgetDataSync) {
        this.suppressSelfWidgetDataSync = false;
        return;
      }

      const nextSignature = JSON.stringify(rows);
      if (nextSignature === this.lastPublishedWidgetDataSignature) {
        return;
      }

      this.updateRowsFromExternalWidgetData(rows);
    });

    return restoredFromWidgetData;
  }

  private updateRowsFromExternalWidgetData(rows: RowData[]): void {
    // Keep configured base rows intact; external rows are the current table state.
    // If we overwrite base rows here, select-all/deselect cycles can duplicate mapped rows.
    this.dataSource = this.dedupeRows(rows.map((row) => this.buildDisplayRow(row)));
    this.normalizeDropdownValuesForRows(this.dataSource);
    this.prefillRowsFromWorkflowCtx(this.dataSource);
  }

  private prefillRowsFromWorkflowCtx(rows: RowData[]): void {
    const workflowCtxMap = this.data?.workflowCtxMap;
    if (!workflowCtxMap || typeof workflowCtxMap !== 'object') {
      return;
    }

    const workflowCtx = this.globalFilterService.getWorkflowCtx<Record<string, any>>() || {};
    const entries = Object.entries(workflowCtxMap as Record<string, any>)
      .map(([columnKey, ctxKey]) => [String(columnKey).trim(), String(ctxKey).trim()] as const)
      .filter(([columnKey, ctxKey]) => columnKey.length > 0 && ctxKey.length > 0);

    if (!entries.length) {
      return;
    }

    rows.forEach((row) => {
      if (!row || typeof row !== 'object') {
        return;
      }

      entries.forEach(([columnKey, ctxKey]) => {
        const currentValue = row[columnKey];
        const ctxValue = workflowCtx[ctxKey];

        const isEmpty =
          currentValue === undefined ||
          currentValue === null ||
          (typeof currentValue === 'string' && currentValue.trim() === '');

        if (!isEmpty) {
          return;
        }

        if (ctxValue === undefined || ctxValue === null) {
          return;
        }

        const stringified = typeof ctxValue === 'string' ? ctxValue : String(ctxValue);
        if (stringified.trim() === '') {
          return;
        }

        row[columnKey] = ctxValue;
      });
    });
  }

  private syncWorkflowCtxForColumn(columnKey: string, value: any): void {
    const workflowCtxMap = this.data?.workflowCtxMap;
    if (!workflowCtxMap || typeof workflowCtxMap !== 'object') {
      return;
    }

    const ctxKeyRaw = (workflowCtxMap as Record<string, any>)[columnKey];
    const ctxKey = typeof ctxKeyRaw === 'string' ? ctxKeyRaw.trim() : '';
    if (!ctxKey) {
      return;
    }

    this.globalFilterService.setWorkflowCtx({ [ctxKey]: value });
  }

  private shouldRetainRowsOnEmptySelection(selectedValues: string[]): boolean {
    const retainOnEmptySelection = this.data?.retainOnEmptySelection === true;
    const hasNoSelection = !Array.isArray(selectedValues) || selectedValues.length === 0;

    return retainOnEmptySelection && hasNoSelection;
  }

  private dedupeRows(rows: RowData[]): RowData[] {
    const skipDedupe =
      this.data?.dedupeRows === false ||
      this.data?.disableRowDedupe === true ||
      this.data?.preserveDuplicateRows === true;
    if (skipDedupe) {
      return Array.isArray(rows) ? rows : [];
    }

    if (!Array.isArray(rows) || rows.length <= 1) {
      return Array.isArray(rows) ? rows : [];
    }

    const seen = new Set<string>();
    const deduped: RowData[] = [];

    rows.forEach((row) => {
      const signature = this.buildRowSignature(row);
      if (seen.has(signature)) {
        return;
      }
      seen.add(signature);
      deduped.push(row);
    });

    return deduped;
  }

  private buildRowSignature(row: RowData): string {
    const source = row && typeof row === 'object' ? row : {};
    const signatureColumns = this.displayedColumns.length > 0
      ? this.displayedColumns
      : Object.keys(source);

    const signatureObject = signatureColumns.reduce((acc: Record<string, any>, columnKey: string) => {
      acc[columnKey] = source[columnKey] ?? '';
      return acc;
    }, {});

    return JSON.stringify(signatureObject);
  }

  private resetTableState(): void {
    this.selectedRowsByValue.clear();
    this.enabledEditableColumns.clear();

    const sourceRows: RowData[] = this.data?.tableData || this.data?.rows || [];
    if (!Array.isArray(sourceRows) || sourceRows.length === 0) {
      this.baseDataSource = [];
      this.dataSource = [];
      this.publishWidgetDataIfEnabled();
      return;
    }

    const emptyRows = sourceRows.map(() => {
      const row: RowData = {};
      this.displayedColumns.forEach((columnKey) => {
        row[columnKey] = this.isMultiSelect(columnKey) ? [] : '';
      });
      return row;
    });

    this.baseDataSource = emptyRows.map((row) => this.buildDisplayRow(row));
    this.dataSource = this.baseDataSource.map((row) => ({ ...row }));
    this.normalizeDropdownValuesForRows(this.dataSource);
    this.publishWidgetDataIfEnabled();
  }

  private publishWidgetDataIfEnabled(): void {
    this.trimDeleteSelection();
    if (!this.data?.publishWidgetData || !this.data?.widgetId) {
      return;
    }

    const rows = this.dataSource.map((row) => ({ ...row }));
    const signature = JSON.stringify(rows);
    if (signature === this.lastPublishedWidgetDataSignature) {
      return;
    }

    this.lastPublishedWidgetDataSignature = signature;
    this.suppressSelfWidgetDataSync = true;
    this.globalFilterService.setWidgetData(this.data.widgetId, rows);
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.widgetDataSubscription?.unsubscribe();
    this.resetStateSubscription?.unsubscribe();
    this.apiDataSubscription?.unsubscribe();
    this.widgetRefreshSubscription?.unsubscribe();
  }
}
